import React from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Printer, Coffee, Monitor, Users, ShieldCheck, 
  Zap, Server, IndianRupee, Handshake, Microscope, GraduationCap, ArrowRight 
} from 'lucide-react';

// Keep your existing image imports
import office2 from "../Facilities/office2.png";
import printing from "../Facilities/Printer_img.jpeg";
import pantry from "../Facilities/p_13.jpg";
import led from "../Facilities/p_14.jpg";
import conf from "../Facilities/conference.png";
import design from "../Facilities/designlab.png";
import bang1 from "../Facilities/officebangalore.png";
import bang2 from "../Facilities/bang2.png";
import work from "../Facilities/working-space.png";
import { FacilitiesList } from "./otherFacilities";

const facilities = [
  { img: conf, title: "Conference Rooms", desc: "High-tech meeting spaces equipped for brainstorming sessions.", icon: Users },
  { img: work, title: "Co-Working Space", desc: "Vibrant open desks fostering collaboration among innovators.", icon: Monitor },
  { img: office2, title: "Private Cabins", desc: "Dedicated, secure office spaces for scaling teams.", icon: MapPin },
  { img: design, title: "Design Lab", desc: "Advanced prototyping lab for hardware testing.", icon: Zap },
  { img: bang1, title: "Bangalore Hub", desc: "Satellite office access at PAN IIT Alumni Centre.", icon: Server },
  { img: bang2, title: "Bangalore Office", desc: "Extended network access in India's Silicon Valley.", icon: MapPin },
  { img: pantry, title: "24x7 Pantry", desc: "Fully stocked cafeteria to fuel your innovation.", icon: Coffee },
  { img: printing, title: "Printing Station", desc: "High-speed printing, scanning, and documentation.", icon: Printer },
  { img: led, title: "Interactive Displays", desc: "55' LED screens and AV systems for pitches.", icon: Monitor },
];

/* ===== Animations ===== */
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
};
  
const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.5, ease: "easeOut" } 
    },
};

function Facilities() {
  return (
    <div className="bg-slate-50 font-sans text-slate-900 selection:bg-blue-100 selection:text-blue-900 overflow-x-hidden">
      
      {/* --- 1. HERO HEADER --- */}
      <div className="relative bg-white pt-32 pb-24 px-6 overflow-hidden">
         
         {/* Background Elements - Subtle Grid */}
         <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
         
         {/* Ambient Glows */}
         <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-50/80 rounded-full blur-[100px] pointer-events-none"></div>
         <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-purple-50/50 rounded-full blur-[100px] pointer-events-none"></div>

         <div className="max-w-5xl mx-auto relative z-10 flex flex-col items-center text-center">
            
            <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 text-blue-600 text-xs font-semibold uppercase tracking-widest mb-6"
            >
               <MapPin size={12} /> World-Class Infrastructure
            </motion.div>

            <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight text-slate-900 drop-shadow-sm"
            >
               Build in a <br className="hidden md:block"/>
               <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-500">
                  Futuristic Environment
               </span>
            </motion.h1>

            <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-slate-600 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-light"
            >
               Access 20,000 sq. ft. of state-of-the-art labs, co-working spaces, and prototyping centers designed to accelerate your startup journey from idea to impact.
            </motion.p>

         </div>
      </div>

      {/* --- 2. FACILITIES GRID --- */}
      <div className="py-24 px-6 max-w-7xl mx-auto">
         <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
         >
            {facilities.map((facility, idx) => (
               <motion.div 
                  key={idx} 
                  variants={cardVariants}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-blue-900/10 hover:-translate-y-2 transition-all duration-300 border border-slate-200"
               >
                  {/* Image Area */}
                  <div className="h-56 overflow-hidden relative">
                     {/* Blue Overlay on Hover */}
                     <div className="absolute inset-0 bg-blue-900/20 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                     
                     <img 
                        src={facility.img} 
                        alt={facility.title} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                     />
                     
                     {/* Gradient Shade for Text Readability (Optional, if text over image) */}
                     {/* <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div> */}
                     
                     <div className="absolute top-4 right-4 z-20">
                        <div className="bg-white/90 backdrop-blur-md p-2 rounded-lg text-blue-600 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                           <facility.icon size={18} />
                        </div>
                     </div>
                  </div>
                  
                  {/* Content Area */}
                  <div className="p-6">
                     <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {facility.title}
                     </h3>
                     <p className="text-slate-500 text-sm leading-relaxed">
                        {facility.desc}
                     </p>
                  </div>
               </motion.div>
            ))}
         </motion.div>
      </div>

      {/* --- 3. OTHER FACILITIES COMPONENT --- */}
      <div className="bg-white py-12 border-t border-slate-100">
         <div className="max-w-7xl mx-auto px-6">
            <FacilitiesList />
         </div>
      </div>

      {/* --- 4. STRATEGIC SUPPORT (Light & Professional) --- */}
      <div className="bg-slate-50 py-24 px-6 relative overflow-hidden border-t border-slate-200">
         
         <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start relative z-10">
            
            {/* Left: Headline & Feature Box */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
               <div className="inline-block px-3 py-1 mb-6 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-bold uppercase tracking-widest">
                  Ecosystem
               </div>
               <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                  Beyond Infrastructure: <br/>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Strategic Support</span>
               </h2>
               <p className="text-slate-600 text-lg leading-relaxed mb-8">
                  We don't just give you a desk; we give you an ecosystem. From specialized defence access to discounted lab rates, TBIF supports your operational needs at every stage.
               </p>
               
               {/* Feature Box (Highlighted) */}
               <div className="p-8 bg-white border border-blue-100 rounded-2xl shadow-lg shadow-blue-500/5 relative overflow-hidden group hover:border-blue-300 transition-colors">
                  <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-3 text-lg">
                     <ShieldCheck className="text-blue-600" size={24}/> Defence Ecosystem Access
                  </h4>
                  <p className="text-slate-500 text-sm leading-relaxed">
                     Exclusive access to the <span className="text-slate-900 font-semibold">Center of Excellence for Defence & Security</span> at IIT Ropar for industry-academia projects and defence procurements.
                  </p>
               </div>
            </motion.div>

            {/* Right: Support Cards Grid */}
            <motion.div 
                className="grid sm:grid-cols-2 gap-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
               <SupportCard 
                  icon={IndianRupee}
                  title="Govt. Funding"
                  desc="Direct channel to Startup Punjab Seed Fund, TIDE 2.0, MSME, EngSUI, and VCF-SC/ST funds."
               />
               <SupportCard 
                  icon={Handshake}
                  title="Investor Network"
                  desc="Matching investments with top VCs like SucSEED, IvyCamp, and India Accelerator."
               />
               <SupportCard 
                  icon={Microscope}
                  title="Subsidized R&D"
                  desc="Utilize IIT Ropar's advanced equipment at internal rates (20-50x cheaper than industry)."
               />
               <SupportCard 
                  icon={GraduationCap}
                  title="Talent Access"
                  desc="Hire interns and researchers directly from the bright talent pool of IIT Ropar."
               />
            </motion.div>

         </div>
      </div>

    </div>
  );
}

// --- SUB-COMPONENT: Support Card (Clean Light Theme) ---
const SupportCard = ({ icon: Icon, title, desc }) => (
   <div className="bg-white border border-slate-200 p-6 rounded-2xl hover:shadow-xl hover:shadow-blue-500/10 hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 group h-full">
      <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500 mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
         <Icon size={22} />
      </div>
      <h4 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors">{title}</h4>
      <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
   </div>
);

export default Facilities;